import { fetchDoctorLatestLabelsForPatients } from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/diagnosis/apptDiagnosisService'

/**
 * Previous-visit label loader (doctor flow): when an upcoming appointment has no Diagnosis/Complaint
 * yet, we surface the patient's most recent PREVIOUS-visit label as a faint hint on the card.
 *
 * Mirrors the per-day diagnosis manager's shape but keyed by PATIENT, not event:
 *  - One bulk call per batch of patients (not one-per-row), de-duped across re-renders.
 *  - Results are kept ONLY in memory (this is PHI and merely a convenience hint — never persisted).
 *  - Every attempted patient is recorded (incl. null = "checked, none") so chips resolve without
 *    per-row fetches. Chips re-read on the update event.
 */

export const PREV_DIAGNOSIS_UPDATE_EVENT = 'aumhum:appt-prev-diagnosis-updated'

// patientId -> { kind, text } (has a label) | null (checked, none). Absent = unknown/not fetched.
const prevByPatient = new Map()
// patientIds already resolved or in flight this session.
const settledOrInFlight = new Set()

function notifyUpdated() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(PREV_DIAGNOSIS_UPDATE_EVENT))
  }
}

/** undefined = not fetched yet, null = checked-none, { kind, text } = has a previous label. */
export function getPreviousDiagnosis(patientId) {
  if (!patientId) return null
  return prevByPatient.get(patientId)
}

/**
 * @param {Array<{patientId: string}>} candidates  patients whose upcoming session lacks a label
 * @param {Array<string>} excludeEventIds           current-day event ids to exclude from "previous"
 */
export async function primePreviousDiagnosesForPatients(candidates, excludeEventIds) {
  if (!Array.isArray(candidates) || candidates.length === 0) return
  try {
    const unseen = [
      ...new Set(
        candidates
          .map((c) => c?.patientId)
          .filter((id) => id && !settledOrInFlight.has(id)),
      ),
    ]
    if (unseen.length === 0) return
    unseen.forEach((id) => settledOrInFlight.add(id))

    let labelByPatient
    try {
      labelByPatient = await fetchDoctorLatestLabelsForPatients(unseen, excludeEventIds)
    } catch (err) {
      // network failure → allow a later prime to retry these patients
      unseen.forEach((id) => settledOrInFlight.delete(id))
      return
    }

    unseen.forEach((id) => {
      const found = labelByPatient.get(id)
      prevByPatient.set(id, found ? { kind: found.kind || 'dx', text: found.text } : null)
    })
    notifyUpdated()
  } catch (err) {
    /* best-effort */
  }
}
